import { ChangeDetectionStrategy, Component, Input } from "@angular/core";
import { IPropertyResponse } from "../../../interfaces/i-property-response";
import moment from "moment";
import { IPropertyStructure } from "../../../interfaces/i-property-structure";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { IPropertyFeatures } from "../../../interfaces/i-property-features";

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: "app-property-info-table",
  templateUrl: "./property-info-table.component.html",
  styleUrls: ["./property-info-table.component.scss"],
  standalone: false,
})
export class PropertyInfoTableComponent {
  roofConditionScale = ["Bad", "Poor", "Fair", "Good", "Great"];

  measurementsSystems = ViewerConstants.MEASUREMENT_SYSTEMS.IDS;

  protected _propertyData: IPropertyResponse;

  structures: IPropertyStructure[];

  csvData = null;

  constructor() {}

  @Input()
  measurementSystem: number;

  @Input() set propertyData(value: IPropertyResponse) {
    this.structures = value.structures || value.structure;
    this.structures?.sort((x, y) => {
      if (x.primaryStructure === y.primaryStructure) {
        return 0;
      }
      if (x.primaryStructure) {
        return -1;
      }
      return 1;
    });
    this._propertyData = value;
    this.csvData = this.propertyToCsv();
  }

  get propertyData(): IPropertyResponse {
    return this._propertyData;
  }

  propertyToCsv(): string {
    let csv = "";

    const headers = this.getHeadersCsv();
    const values = this.getValuesCsv();

    csv += `${headers}\n${values}`;
    return csv;
  }

  getHeadersCsv(): string {
    let headers =
      "Parcel Boundary,Parcel Analysis Date,Pool,Pool Area,Diving board,Waterslide,Deck,Enclosure,Trampoline,Playground,Sportcourt";
    this.structures?.forEach(() => {
      headers +=
        "Building Footprint,Primary Structure,Footprint Area,Building Tree Cover,Rooftop Geocode,Rooftop Shape,Roof Material,Roof Condition,Roof Solar";
    });
    return headers;
  }

  /**
   * Generates a CSV string with property features and structures data.
   *
   * @returns {string} The CSV string.
   */
  getValuesCsv(): string {
    const features = this.propertyData?.property_features;
    let values = "";

    if (features) {
      values += this.getPropertyFeaturesCsv(features);
    }

    if (!!this.structures?.length) {
      values += this.getStructuresCsv(this.structures);
    }

    return values;
  }

  /**
   * Generates a CSV string from property features.
   *
   * @param {PropertyFeatures} features - The property features data.
   * @returns {string} The CSV string for the property features.
   */
  private getPropertyFeaturesCsv(features: IPropertyFeatures): string {
    return (
      [
        features.parcel_wkt || "",
        this.extractCaptureDate(this._propertyData),
        this.booleanToYesNo(features.pool?.detected),
        features.pool?.detected ? features.pool.score : "No",
        this.booleanToYesNo(features.diving_board?.detected),
        this.booleanToYesNo(features.water_slide?.detected),
        this.booleanToYesNo(features.deck?.detected),
        this.booleanToYesNo(features.enclosure?.detected),
        this.booleanToYesNo(features.trampoline?.detected),
        this.booleanToYesNo(features.playground?.detected),
        this.booleanToYesNo(features.sport_court?.detected),
      ].join(",") + ","
    );
  }

  /**
   * Generates a CSV string from structures data.
   *
   * @param {Structure[]} structures - The structures data array.
   * @returns {string} The CSV string for the structures.
   */
  private getStructuresCsv(structures: IPropertyStructure[]): string {
    return structures
      .map((structure) => {
        return [
          structure.footPrint?.wkt || "",
          this.booleanToYesNo(structure.primaryStructure),
          `${structure.footPrint?.area || 0}m2`,
          `${structure.tree_cover?.percent || 0}%`,
          structure.roof_top_location?.wkt || "",
          structure.roof_shape?.value || "",
          `${structure.roof_shape?.flatPercent || 0}%`,
          structure.roof_material?.class || "",
          structure.roof_condition?.value || "",
          structure.roof_solar?.value === "false" ? "No" : structure.roof_solar?.score || "",
        ].join(",");
      })
      .join(",");
  }

  /**
   * Converts a boolean value to "Yes" or "No".
   *
   * @param {boolean | undefined} value - The boolean value to convert.
   * @returns {string} "Yes" if true, "No" if false or undefined.
   */
  private booleanToYesNo(value?: boolean): string {
    return value ? "Yes" : "No";
  }

  getTooltipPropertyFeature(feature: any): string {
    if (feature?.score === null) return null;
    return feature?.score ? `Confidence score: ${(feature.score * 100).toFixed(2)}%` : "Confidence score: 0%";
  }

  getRoofConditionFormatted(value: number): string {
    return `${value} = ${this.roofConditionScale[value - 1]} condition`;
  }

  getRoofConditionTooltip(value: number): string {
    switch (value) {
      case 1:
        return "Pronounced signs of defects that significantly affect the function and safety of the roof. The damage is beyond the barrier or surface materials.";
      case 2:
        return "Pronounced signs of defects that affect the function and safety of the roof. The damage is primarily at the barrier or surface materials.";
      case 3:
        return "Discoloration and damage are present but not as pronounced as scores 1 and 2. Defects may affect the function and safety of the roof if not addressed.";
      case 4:
        return "Minimal signs of defects. Defects are cosmetic and do not affect the function and safety of the roof.";
      case 5:
        return "No visible signs of defects. It looks new.";
      default:
        return "";
    }
  }

  getDefensibleSpaceValue(value: any): string {
    return value ? `${Math.round(value).toString()}%` : "-";
  }

  formatFeatureInfo(feature: any): string {
    if (feature.detected === null) {
      return "-";
    }
    return feature.detected ? `Yes` : "No";
  }

  getFeatureTypes(types: any): string[] {
    return types !== null ? Object.keys(types) : [];
  }

  extractCaptureDate(data: IPropertyResponse): string {
    if (data.capture_date) {
      return this.formatDate(data.capture_date);
    }
    if (data.ortho_date_estimate) {
      return this.formatDate(data.ortho_date_estimate);
    }

    return null;
  }

  formatDate(date: string): string {
    return moment(date).format("DD-MMM-YYYY").toUpperCase();
  }

  fixFormat(value: any): string {
    return Number(value).toFixed(1).toString();
  }

  getGeometryType(wkt: string): string {
    if (!wkt) return "";
    const idx = wkt.indexOf("(");
    return idx > -1 ? wkt.substring(0, idx).trim() : wkt;
  }

  formatKey(key: string): string {
    const partKeys = key.split("_");
    let formatEntry = "";
    partKeys.forEach((p) => {
      formatEntry += p + " ";
    });
    return formatEntry.charAt(0).toUpperCase() + formatEntry.slice(1);
  }

  calculateTotalBuildingArea() {
    let totalArea = 0;
    this.structures.forEach((structure) => {
      totalArea = totalArea + structure.footPrint.area;
    });

    return totalArea;
  }
}
